import { v4 as uuidv4 } from 'uuid';

// Asset upload related types
export type StagedFileStatus = 'ready' | 'creating' | 'complete' | 'error';
export type BatchStatus = 'idle' | 'processing' | 'finished';
export type StagedFileType = 'vrm' | 'vrma' | 'fbx' | 'dae' | 'gltf' | 'bin' | 'md' | 'jpeg' | 'jpg' | 'png' | 'gif' | 'webp' | 'svg' | 'mp4' | 'webm' | 'mov' | 'txt' | 'riv' | 'csv' | 'json' | 'unknown';

// Define a dependency file type that doesn't have its own dependencies
export type DependencyFile = Omit<StagedFile, 'dependencies'>;

export interface StagedFile {
    id: string;
    file: File;
    name: string;
    status: StagedFileStatus;
    type: StagedFileType;
    error?: string;
    finalAssetUuid?: string;
    // Dependencies stored as key-value pairs (dependency key -> dependency file)
    dependencies?: Record<string, DependencyFile>; // For main files: map of dependency keys to dependency files (without nested dependencies)
    isCollapsed: boolean;  // UI state stored with the file
}

// Helper function to determine file type from filename
export const getFileTypeFromName = (fileName: string): StagedFileType => {
    const lowerCaseFileName = fileName.toLowerCase();
    if (lowerCaseFileName.endsWith('.vrm')) {
        return 'vrm';
    } else if (lowerCaseFileName.endsWith('.vrma')) {
        return 'vrma';
    } else if (lowerCaseFileName.endsWith('.fbx')) {
        return 'fbx';
    } else if (lowerCaseFileName.endsWith('.dae')) {
        return 'dae';
    } else if (lowerCaseFileName.endsWith('.gltf')) {
        return 'gltf';
    } else if (lowerCaseFileName.endsWith('.bin')) {
        return 'bin';
    } else if (lowerCaseFileName.endsWith('.md') || lowerCaseFileName.endsWith('.markdown')) {
        return 'md';
    } else if (lowerCaseFileName.endsWith('.jpeg')) {
        return 'jpeg';
    } else if (lowerCaseFileName.endsWith('.jpg')) {
        return 'jpg';
    } else if (lowerCaseFileName.endsWith('.png')) {
        return 'png';
    } else if (lowerCaseFileName.endsWith('.gif')) {
        return 'gif';
    } else if (lowerCaseFileName.endsWith('.webp')) {
        return 'webp';
    } else if (lowerCaseFileName.endsWith('.svg')) {
        return 'svg';
    } else if (lowerCaseFileName.endsWith('.mp4')) {
        return 'mp4';
    } else if (lowerCaseFileName.endsWith('.webm')) {
        return 'webm';
    } else if (lowerCaseFileName.endsWith('.mov')) {
        return 'mov';
    } else if (lowerCaseFileName.endsWith('.txt')) {
        return 'txt';
    } else if (lowerCaseFileName.endsWith('.riv')) {
        return 'riv';
    } else if (lowerCaseFileName.endsWith('.csv')) {
        return 'csv';
    } else if (lowerCaseFileName.endsWith('.json')) {
        return 'json';
    }
    return 'unknown';
};

// Map file type to corresponding asset type
export const fileTypeToAssetTypeMap: Record<StagedFileType, string> = {
    vrma: 'animation/vrma+gltf',
    vrm: 'model/vrm+gltf',
    fbx: 'model/fbx',
    dae: 'model/vnd.collada+xml',
    gltf: 'model/gltf+json',
    bin: 'application/octet-binary',
    md: 'text/markdown',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    mp4: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
    txt: 'text/plain',
    riv: 'application/rive',
    csv: 'text/csv',
    json: 'application/json',
    unknown: '', // Should not be used
};

// Helper function to create a new StagedFile from a File object
export const createStagedFile = (file: File): StagedFile => {
    const fileType = getFileTypeFromName(file.name);
    
    if (fileType === 'unknown') {
        return { 
            id: uuidv4(), 
            file, 
            name: file.name.replace(/\.[^/.]+$/, ""), 
            status: 'error', 
            type: 'unknown', 
            error: 'Only .vrm, .vrma, .fbx, .dae, .gltf, .md, .markdown, .jpeg, .jpg, .png, .gif, .webp, .svg, .mp4, .webm, .mov, .txt, .riv, .csv, or .json files are allowed.', 
            isCollapsed: false,
        };
    }

    if (file.size > 25 * 1024 * 1024) { // 25MB limit
        return { 
            id: uuidv4(), 
            file, 
            name: file.name.replace(/\.[^/.]+$/, ""), 
            status: 'error', 
            type: fileType, 
            error: 'File size exceeds 25MB.' ,
            isCollapsed: false,
        };
    }
    
    // Additional CSV-specific validation
    if (fileType === 'csv' && file.type !== '' && !file.type.includes('csv') && !file.type.includes('plain')) {
        // For CSV files, we'll also check if the content looks like CSV
        // This is a basic check - we can expand this in the future
    }
    
    return { 
        id: uuidv4(), 
        file, 
        name: file.name.replace(/\.[^/.]+$/, ""), 
        status: 'ready', 
        type: fileType,
        dependencies: {},
        isCollapsed: false
    };
};